import { useEffect, useMemo, useRef, useState } from "react";
import { t } from "../locale";
import {
  fetchSessions,
  fetchOfferingSlots,
  type SessionPublic,
  type OfferingSlotsResponse,
} from "../../lib/sessionsApi";
import { useEventTimezone } from "../theme/WBThemeRuntimeProvider";
import {
  WB_VALIDATE_EVENT,
  WB_SESSION_FULL_EVENT,
  WB_OFFERING_CONFLICT_EVENT,
  WB_SUBMIT_SUCCESS_EVENT,
  resolveFieldSpacing,
} from "./shared";
import { SlotPicker, type SlotSelection } from "./slotPicker";
import { reportValidationError, shouldValidateField } from "./formInput";

/* Local tailwind-token parsers — same house pattern as bookingGroup.tsx. */
function parseBorderRadiusPxLocal(tw: string): number {
  const map: Record<string, number> = {
    "rounded-none": 0, "rounded-sm": 2, "rounded": 4, "rounded-md": 6,
    "rounded-lg": 8, "rounded-xl": 12, "rounded-2xl": 16, "rounded-3xl": 24,
  };
  return map[tw] ?? 8;
}

function parsePaddingPx(tw?: string): string | undefined {
  if (!tw) return undefined;
  const map: Record<string, string> = {
    "p-0": "0", "p-1": "4px", "p-2": "8px", "p-3": "12px",
    "p-4": "16px", "p-5": "20px", "p-6": "24px", "p-8": "32px",
  };
  return map[tw];
}

/** Descriptions longer than this collapse behind a "show more" toggle. */
const DESCRIPTION_COLLAPSE_CHARS = 180;

/** The governed session envelope — wire twin of the worker's
 *  `SessionSelectionInput` governed mode, mirroring `offeringBookings`. */
export type SessionSelectionPayload = {
  /** The SESSION blocks THIS form instance renders. The worker's release
   *  is scoped to exactly these, so a holding on a block this form never
   *  displayed (filtered widget, multi-form event, >LIST_LIMIT programme)
   *  is structurally unreachable by this submit. */
  governedProgramItemIds: string[];
  /** The guest's desired picks within the governed set. */
  selected: string[];
};

/**
 * Collect the session selection from the form's hidden
 * `__session_selected` inputs.
 *
 * - No input present (no SessionGroup mounted / list failed to load) →
 *   `undefined` → the submit payload OMITS `sessions` → the worker leaves
 *   the guest's existing picks untouched.
 * - JSON value → the GOVERNED envelope. Emitted whenever the worker
 *   advertised seed support, so the widget knows what the guest already
 *   holds and a release is meaningful.
 * - CSV value → the LEGACY bare array, emitted only against a worker with
 *   no seed support. The worker treats it as acquire-only: without a seed
 *   the widget cannot tell "untouched" from "cleared", so it is not
 *   allowed to release anything.
 *
 * Multiple SessionGroups union both arrays.
 */
export function collectSessionsPayload(
  form: HTMLFormElement,
): string[] | SessionSelectionPayload | undefined {
  const inputs = form.querySelectorAll<HTMLInputElement>(
    'input[name="__session_selected"]',
  );
  if (inputs.length === 0) return undefined;

  const selectedSeen = new Set<string>();
  const selected: string[] = [];
  const governedSeen = new Set<string>();
  const governed: string[] = [];
  let governedMode = false;

  const pushSelected = (id: string) => {
    if (id && !selectedSeen.has(id)) {
      selectedSeen.add(id);
      selected.push(id);
    }
  };

  for (const el of Array.from(inputs)) {
    const raw = el.value.trim();
    if (raw.startsWith("{")) {
      // Governed envelope. A malformed value is skipped rather than
      // throwing — same fail-open rule as the offering collector.
      try {
        const parsed = JSON.parse(raw) as Partial<SessionSelectionPayload>;
        if (
          Array.isArray(parsed.governedProgramItemIds) &&
          Array.isArray(parsed.selected)
        ) {
          governedMode = true;
          for (const id of parsed.governedProgramItemIds) {
            if (typeof id === "string" && id && !governedSeen.has(id)) {
              governedSeen.add(id);
              governed.push(id);
            }
          }
          for (const id of parsed.selected) {
            if (typeof id === "string") pushSelected(id);
          }
        }
      } catch {
        // skip this input
      }
      continue;
    }
    for (const part of raw.split(",")) pushSelected(part.trim());
  }

  if (governedMode) {
    // Any widget that emitted the governed envelope makes the whole submit
    // governed. Ids contributed by a LEGACY (CSV) sibling were never
    // declared as governed by anyone, so folding them in would violate the
    // worker's `selected ⊆ governed` rule and 400 the entire submit — the
    // guest's field edits included. Drop them: an ungoverned id cannot be
    // released either way, so nothing is lost by omitting it.
    const governedSet = new Set(governed);
    return {
      governedProgramItemIds: governed,
      selected: selected.filter((id) => governedSet.has(id)),
    };
  }
  return selected;
}

/**
 * True when ANY mounted SessionGroup reports its session picks as changed
 * against the seeded (server) state — twin of
 * {@link hasOfferingSelectionChanges}. dataInput's edit-mode "noChanges"
 * gate consults this: a guest who changes ONLY their session picks has
 * identical field values, so without it the submit silently no-ops.
 */
export function hasSessionSelectionChanges(form: HTMLFormElement): boolean {
  const inputs = form.querySelectorAll<HTMLInputElement>(
    'input[name="__session_dirty"]',
  );
  for (const el of Array.from(inputs)) {
    if (el.value === "1") return true;
  }
  return false;
}

/** One booking of the `__offering_selected` hidden-input JSON — wire twin
 *  of the worker's `offeringBookings.bookings[]` element (1:n pivot). */
export type OfferingBookingEntry = {
  programItemId: string;
  /** REQUIRED — the chosen treatment ("Terminart") within the block's
   *  container; the worker 400s rows without it, so the collector skips
   *  treatmentless rows defensively. */
  treatmentId: string;
  laneId: string;
  startDt: string;
  endDt: string;
};

/** Wire twin of the worker's `DataInputBody.offeringBookings` object
 *  (runtime.ts, governed-scope contract). */
export type OfferingBookingsPayload = {
  /** The OFFERING blocks THIS form instance governs — the worker's cancel
   *  sweep touches ONLY these. A block is governed once its slot envelope
   *  loaded, it is AUTO_BOOKABLE (REQUEST_BASED never — PB-WB-5) AND any
   *  existing appointment is representable (laneId + treatmentId present);
   *  everything else stays out, so its booking can never be swept by this
   *  submit. */
  governedProgramItemIds: string[];
  /** Desired COMPLETE booking set WITHIN the governed blocks (must be a
   *  subset — the worker 400s otherwise). `[]` = cancel the governed
   *  blocks' managed bookings only. */
  bookings: OfferingBookingEntry[];
};

/**
 * Collect the OFFERING booking payload from the form's hidden
 * `__offering_selected` inputs — the analogue of
 * {@link collectSessionsPayload} for `offeringBookings` (governed-scope
 * contract, W2 R1).
 *
 * - No input present (no OFFERING cards mounted / no block governed) →
 *   `undefined` → the submit payload OMITS `offeringBookings` → the
 *   worker leaves the guest's existing bookings untouched.
 * - Input(s) present → `{governedProgramItemIds, bookings}`; bookings are
 *   defensively filtered to the governed set (subset invariant) and
 *   deduped by programItemId (the worker rejects duplicates as
 *   ambiguous). Multiple inputs (future MAX_SESSION_GROUPS > 1) union.
 *
 * Malformed input values are skipped; if NO input parsed successfully —
 * or the union governs no block — the collector returns `undefined`
 * (fail-open: an unparsable state must never degrade into a sweep).
 */
export function collectOfferingBookingsPayload(
  form: HTMLFormElement,
): OfferingBookingsPayload | undefined {
  const inputs = form.querySelectorAll<HTMLInputElement>(
    'input[name="__offering_selected"]',
  );
  if (inputs.length === 0) return undefined;
  const governed = new Set<string>();
  const seenBookings = new Set<string>();
  const bookings: OfferingBookingEntry[] = [];
  let parsedAny = false;
  for (const el of Array.from(inputs)) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(el.value === "" ? "null" : el.value);
    } catch {
      continue;
    }
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed))
      continue;
    const rec = parsed as Record<string, unknown>;
    const rawGoverned = rec.governedProgramItemIds;
    const rawBookings = rec.bookings;
    if (!Array.isArray(rawGoverned) || !Array.isArray(rawBookings)) continue;
    parsedAny = true;
    for (const id of rawGoverned) {
      if (typeof id === "string" && id !== "") governed.add(id);
    }
    for (const raw of rawBookings) {
      if (raw === null || typeof raw !== "object") continue;
      const r = raw as Record<string, unknown>;
      if (
        typeof r.programItemId !== "string" ||
        r.programItemId === "" ||
        // 1:n pivot: rows lacking a treatmentId are unbookable (the worker
        // 400s the whole submit on them) — skip defensively.
        typeof r.treatmentId !== "string" ||
        r.treatmentId === "" ||
        typeof r.laneId !== "string" ||
        typeof r.startDt !== "string" ||
        typeof r.endDt !== "string"
      ) {
        continue;
      }
      if (seenBookings.has(r.programItemId)) continue;
      seenBookings.add(r.programItemId);
      bookings.push({
        programItemId: r.programItemId,
        treatmentId: r.treatmentId,
        laneId: r.laneId,
        startDt: r.startDt,
        endDt: r.endDt,
      });
    }
  }
  if (!parsedAny || governed.size === 0) return undefined;
  return {
    governedProgramItemIds: Array.from(governed),
    // Subset invariant: a booking outside the governed union would 400
    // the whole submit — drop it here instead.
    bookings: bookings.filter((b) => governed.has(b.programItemId)),
  };
}

/**
 * True when ANY mounted SessionGroup reports its OFFERING selections as
 * changed against the seeded/initial (server) state — the widget keeps a
 * per-block baseline captured at seed time and serialises the comparison
 * into a hidden `__offering_dirty` input ("1"/"0"). dataInput's edit-mode
 * "noChanges" gate consults this (F2): a guest changing ONLY their slot
 * has identical field values, so without it the submit would silently
 * no-op and never reach the worker's booking diff. No input mounted →
 * false (nothing governed → nothing can have changed).
 */
export function hasOfferingSelectionChanges(form: HTMLFormElement): boolean {
  const inputs = form.querySelectorAll<HTMLInputElement>(
    'input[name="__offering_dirty"]',
  );
  for (const el of Array.from(inputs)) {
    if (el.value === "1") return true;
  }
  return false;
}

/** Per-OFFERING-block slot-envelope fetch state. */
type OfferingSlotState = {
  loading: boolean;
  error: boolean;
  data: OfferingSlotsResponse | null;
};

/** A picked slot plus the treatment ("Terminart") it belongs to — the
 *  block-level selection unit. The SlotPicker stays treatment-agnostic;
 *  the owning card stamps the envelope's `selectedTreatmentId` on every
 *  pick (booking rows REQUIRE treatmentId, 1:n pivot). */
type OfferingSelection = SlotSelection & { treatmentId: string };

type SessionGroupProps = {
  /* ---- content contract (editor twin builds the same prop names) ---- */
  /** "multiple" (checkbox-like, default) or "single" (radio-like). */
  selectionMode?: "single" | "multiple";
  /** Minimum number of sessions the registrant must pick (0/undefined = optional). */
  min?: number;
  /** Maximum number of picks (undefined = unlimited; single mode caps at 1). */
  max?: number;
  /** Restrict to these program items; empty/undefined = all sessions. */
  includedProgramItemIds?: string[];
  showSpeakers?: boolean;
  showDescription?: boolean;
  /* ---- group chrome (BookingGroup family) ---- */
  isVisible?: boolean;
  groupLabel?: string;
  groupLabelColor?: string;
  backgroundColor?: string;
  padding?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: string[];
  borderRadius?: string;
  fieldSpacing?: string;
  cardBorderRadius?: string;
  selectionBorderColor?: string;
  selectionActiveBorderColor?: string;
  selectionActiveBackgroundColor?: string;
};

/**
 * SessionGroup — the published-site session-selection step (Cvent-model
 * "Programm" agenda picker) rendered inside DataInput.
 *
 * Live-fetches GET /sessions on mount (bookingGroup idiom), renders
 * select-cards grouped by wall-clock day in the event timezone, exposes the
 * chosen set through a hidden `__session_selected` input, and enforces
 * min/max + not-sold-out on WB_VALIDATE_EVENT.
 *
 * PREFILL (allowUpdate): `GET /sessions` carries a per-row `selected` flag
 * for a cookie-authenticated reader, and the picker seeds `selectedIds`
 * from it ONCE. Before that seed existed, an updating registrant started
 * empty and a submit re-emitted that empty set as the guest's complete
 * desired state — so changing only an unrelated field (a phone number)
 * deleted every session registration and released the seats, with a green
 * success toast.
 *
 * The `selected` KEY's presence is a capability probe: an old worker omits
 * it entirely, and against such a worker the picker falls back to the
 * legacy CSV wire shape, which the worker treats as acquire-only. That is
 * what keeps not-yet-republished sites safe — see `collectSessionsPayload`.
 */
export function SessionGroup({
  selectionMode,
  min,
  max,
  includedProgramItemIds,
  showSpeakers,
  showDescription,
  isVisible,
  groupLabel,
  groupLabelColor,
  backgroundColor,
  padding,
  border,
  borderColor,
  borderWidth,
  borderStyle,
  borderSides,
  borderRadius,
  fieldSpacing,
  cardBorderRadius,
  selectionBorderColor,
  selectionActiveBorderColor,
  selectionActiveBackgroundColor,
}: SessionGroupProps) {
  const [sessions, setSessions] = useState<SessionPublic[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /**
   * The guest's picks as the SERVER last confirmed them. Two jobs:
   * `__session_dirty` compares against it, and the sold-out validator
   * exempts it (a guest holding the last seat of a capacity-1 session reads
   * `spotsLeft: 0`, and blocking that would lock them out of saving ANY
   * edit — strictly worse than the bug the seed fixes).
   */
  const [sessionBaseline, setSessionBaseline] = useState<Set<string>>(
    new Set(),
  );
  /**
   * Whether the worker advertised seed support (`selected` present on the
   * rows). Drives the wire shape: governed envelope when true, legacy CSV
   * when false.
   */
  const [seedKnown, setSeedKnown] = useState(false);
  /**
   * ONE-SHOT seed latch. Deliberately a ref, and deliberately not derived
   * from `sessions`: `handleSessionFull` refetches the list, and a
   * seed-on-every-list would resurrect a pick the guest just deselected.
   */
  const seededRef = useRef(false);
  /** Server-confirmed full ids from a 409 sessionFull submit. */
  const [fullIds, setFullIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [groupError, setGroupError] = useState<string | null>(null);
  /* ---- OFFERING slot booking (W3) ---- */
  const [offeringStates, setOfferingStates] = useState<
    Map<string, OfferingSlotState>
  >(new Map());
  const [offeringSelections, setOfferingSelections] = useState<
    Map<string, OfferingSelection>
  >(new Map());
  /** Last-known SERVER state per block (the seed) — what a submit that
   *  changes nothing would re-emit. `__offering_dirty` compares the live
   *  selections against this (F2: slot-only edits must defeat dataInput's
   *  "noChanges" gate). Updated on every successful envelope response and
   *  re-baselined to the submitted set on WB_SUBMIT_SUCCESS_EVENT. */
  const [offeringBaselines, setOfferingBaselines] = useState<
    Map<string, OfferingSelection>
  >(new Map());
  /** F1: blocks whose selection the guest EXPLICITLY removed (remove
   *  button / toggling the picked slot off). The seed guard skips
   *  re-seeding these — pill browsing refetches the envelope, and without
   *  the flag the still-present `myAppointment` would silently resurrect
   *  the removed row (undoing the cancel intent). Cleared on a fresh pick,
   *  on reseed-after-conflict (server state wins) and on successful
   *  submit (the cancel is persisted; a later appointment — e.g. admin
   *  booked — must seed again). Ref, not state: never rendered. */
  const explicitlyRemovedRef = useRef<Set<string>>(new Set());
  const [expandedOfferings, setExpandedOfferings] = useState<Set<string>>(
    new Set(),
  );
  const requestedOfferingIds = useRef<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const eventTz = useEventTimezone();

  const isSingle = selectionMode === "single";
  const effectiveMin = typeof min === "number" && min > 0 ? min : 0;
  const effectiveMax = isSingle
    ? 1
    : typeof max === "number" && max > 0
      ? max
      : null;

  useEffect(() => {
    let active = true;
    // Opt into ALL block kinds — this widget renders OFFERING slot cards
    // and BREAK schedule rows. Old baked bundles keep calling bare
    // /sessions (SESSION-only default filter) — that asymmetry is the
    // designed protection for stale per-event snapshots.
    fetchSessions(["SESSION", "OFFERING", "BREAK"])
      .then((list) => {
        if (!active) return;
        setSessions(list);
        // Capability probe — the KEY's presence, never its value. An old
        // worker omits `selected` entirely; a new one always emits it for
        // an authenticated reader, `false` included.
        setSeedKnown(list.some((s) => "selected" in s));
        setLoadError(null);
      })
      .catch((err) => {
        if (!active) return;
        setLoadError(
          err instanceof Error ? err.message : t("sessionsUnavailable"),
        );
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Visible sessions: optional include-list filter, then chronological.
  const visibleSessions = useMemo(() => {
    if (!sessions) return [];
    // `Array.isArray`, NOT a length check: absent/undefined = no
    // restriction, explicit `[]` = NOTHING offered. The length check meant
    // an empty include-list silently offered EVERY session on the live site
    // while the editor canvas showed none. Same convention the sponsors
    // binding settled on (`filterSponsorsForSection`).
    const included = Array.isArray(includedProgramItemIds)
      ? new Set(includedProgramItemIds)
      : null;
    const filtered = included
      ? sessions.filter((s) => included.has(s.programItemId))
      : sessions;
    return [...filtered].sort((a, b) => {
      if (a.startTime === b.startTime) return 0;
      if (a.startTime === null) return 1;
      if (b.startTime === null) return -1;
      return a.startTime < b.startTime ? -1 : 1;
    });
  }, [sessions, includedProgramItemIds]);

  const sessionById = useMemo(() => {
    const map = new Map<string, SessionPublic>();
    for (const s of visibleSessions) map.set(s.programItemId, s);
    return map;
  }, [visibleSessions]);

  /**
   * The SESSION blocks this instance governs — the release scope handed to
   * the worker, twin of `governedOfferingIds`. Keyed off `visibleSessions`,
   * so an `includedProgramItemIds` filter narrows it: holdings on blocks
   * this form never displayed can no longer be swept by its submit.
   *
   * Empty until the seed capability is known — governing blocks whose
   * current state we cannot see would be exactly the wipe this fixes.
   */
  const governedSessionIds = useMemo(
    () =>
      seedKnown
        ? visibleSessions
            .filter((s) => s.kind === "SESSION")
            .map((s) => s.programItemId)
        : [],
    [visibleSessions, seedKnown],
  );

  /**
   * ONE-SHOT seed of the guest's existing picks. Runs on the first render
   * where the (filtered) list is known; `seededRef` keeps a later refetch —
   * notably `handleSessionFull`'s — from resurrecting a deselected pick.
   */
  useEffect(() => {
    if (seededRef.current) return;
    if (!sessions) return;
    // The latch must burn only once we can ACTUALLY seed. Burning it before
    // this guard meant a `sessions` update that arrived while `seedKnown`
    // was still false (the 409 refetch racing a pending mount fetch) used
    // up the one shot, so the real list never seeded and the widget then
    // submitted `selected: []` while governing everything — the exact wipe
    // this whole change exists to prevent.
    if (!seedKnown) return;
    seededRef.current = true;
    const held = new Set(
      visibleSessions
        .filter((s) => s.kind === "SESSION" && s.selected === true)
        .map((s) => s.programItemId),
    );
    if (held.size === 0) return;
    setSelectedIds(held);
    setSessionBaseline(held);
  }, [sessions, visibleSessions, seedKnown]);

  /** Live picks vs the server baseline — serialised into `__session_dirty`
   *  so a session-only edit defeats dataInput's "noChanges" gate. */
  const sessionDirty = useMemo(() => {
    if (selectedIds.size !== sessionBaseline.size) return true;
    for (const id of selectedIds) if (!sessionBaseline.has(id)) return true;
    return false;
  }, [selectedIds, sessionBaseline]);

  // Group by wall-clock day in the event timezone (Cvent agenda headers).
  const dayGroups = useMemo(() => {
    const groups = new Map<string, SessionPublic[]>();
    for (const s of visibleSessions) {
      const key = s.startTime
        ? new Date(s.startTime).toLocaleDateString(undefined, {
            timeZone: eventTz,
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : "";
      const bucket = groups.get(key);
      if (bucket) bucket.push(s);
      else groups.set(key, [s]);
    }
    return Array.from(groups.entries());
  }, [visibleSessions, eventTz]);

  /* ---- OFFERING slot machinery (W3) ---- */

  const visibleOfferings = useMemo(
    () => visibleSessions.filter((s) => s.kind === "OFFERING"),
    [visibleSessions],
  );

  /**
   * Load one block's slot envelope, optionally scoped to a treatment
   * (`?treatmentId=` — the two-step pick's refetch; the envelope's
   * `selectedTreatmentId` then drives which slots the picker shows).
   *
   * `reseed` (post-conflict) replaces any stale pick with the guest's
   * SURVIVING appointment — or clears it, forcing a conscious re-pick.
   * The failed submit left no durable offering change server-side: the
   * worker compensates created bookings (reinstating reschedule-replaced
   * ones best-effort), and governed sweep-cancels are plan-only until the
   * guest tx commits (`executeSweepCancels` runs post-commit), so a
   * rejected submit never cancels an existing booking. Risk #5
   * (pill/slots desync): when the bare reseed fetch reveals a surviving
   * appointment whose treatment is NOT the envelope's selected one (n>1 →
   * `selectedTreatmentId` null), a follow-up fetch scoped to the
   * survivor's treatmentId re-syncs pill + slots. The follow-up passes an
   * explicit treatmentId, so it can never chain again (no loop).
   */
  function loadOfferingSlots(
    programItemId: string,
    opts: { treatmentId?: string; reseed?: boolean } = {},
  ): void {
    const { treatmentId, reseed = false } = opts;
    setOfferingStates((prev) => {
      const next = new Map(prev);
      next.set(programItemId, {
        loading: true,
        error: false,
        data: prev.get(programItemId)?.data ?? null,
      });
      return next;
    });
    fetchOfferingSlots(programItemId, treatmentId)
      .then((resp) => {
        const my = resp.myAppointment;
        if (
          reseed &&
          treatmentId === undefined &&
          my !== null &&
          my.treatmentId !== null &&
          resp.selectedTreatmentId !== my.treatmentId
        ) {
          // Surviving appointment lives on a treatment the bare envelope
          // did not scope to — refetch scoped so the preselected pill and
          // the shown slots agree. Seeding happens on the follow-up.
          loadOfferingSlots(programItemId, {
            treatmentId: my.treatmentId,
            reseed: true,
          });
          return;
        }
        setOfferingStates((prev) =>
          new Map(prev).set(programItemId, {
            loading: false,
            error: false,
            data: resp,
          }),
        );
        // The block's last-known SERVER state: the guest's representable
        // appointment as a booking row, else nothing.
        const serverSeed: OfferingSelection | null =
          my !== null && my.laneId !== null && my.treatmentId !== null
            ? {
                treatmentId: my.treatmentId,
                laneId: my.laneId,
                startDt: my.startDt,
                endDt: my.endDt,
              }
            : null;
        // Baseline (F2): every successful envelope re-anchors the
        // change-detection baseline at the server state — the value a
        // no-touch submit would re-emit. `__offering_dirty` compares the
        // live selections against this.
        setOfferingBaselines((prev) => {
          const next = new Map(prev);
          if (serverSeed !== null) next.set(programItemId, serverSeed);
          else next.delete(programItemId);
          return next;
        });
        // Reseed-after-conflict: server state wins outright — an
        // "explicitly removed" flag from before the conflict is void (F1).
        if (reseed) explicitlyRemovedRef.current.delete(programItemId);
        const removed = explicitlyRemovedRef.current.has(programItemId);
        // Seed the pick from the guest's current appointment (INCLUDING
        // its treatmentId — booking rows require it) so a resubmit
        // preserves (rather than cancels) the existing booking —
        // offeringBookings is a COMPLETE desired set. Never overwrite an
        // explicit pick unless reseeding after a conflict — and never
        // resurrect an EXPLICITLY REMOVED one (F1: pill browsing refetches
        // this envelope; `myAppointment` is still present server-side
        // until the cancel is submitted, so re-seeding would silently undo
        // the guest's remove). A treatmentless (legacy/cockpit)
        // appointment is UNREPRESENTABLE as a booking row → no seed; the
        // block is also ungoverned (see isOfferingGoverned) so the
        // unseeded state can never sweep-cancel it.
        setOfferingSelections((prev) => {
          if (!reseed && (prev.has(programItemId) || removed)) return prev;
          const next = new Map(prev);
          if (serverSeed !== null) next.set(programItemId, serverSeed);
          else next.delete(programItemId);
          return next;
        });
      })
      .catch(() => {
        setOfferingStates((prev) =>
          new Map(prev).set(programItemId, {
            loading: false,
            error: true,
            data: null,
          }),
        );
      });
  }

  // Prefetch every visible OFFERING block's slot envelope as soon as the
  // list is known. Deliberate deviation from pure expand-time laziness:
  // a block only becomes GOVERNED (sweepable/bookable via this form) once
  // its envelope — and therefore `myAppointment` — is known. Expansion
  // (the heavy UI) stays lazy.
  useEffect(() => {
    for (const s of visibleOfferings) {
      if (requestedOfferingIds.current.has(s.programItemId)) continue;
      requestedOfferingIds.current.add(s.programItemId);
      loadOfferingSlots(s.programItemId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleOfferings]);

  /**
   * Per-block governance (governed-scope wire contract): a block is
   * governed when its slot envelope loaded successfully AND the block is
   * directly bookable (AUTO_BOOKABLE — a REQUEST_BASED block has no booking
   * and no cancel UI, so the form can never express a change for it;
   * PB-WB-5) AND the guest does NOT hold multiple appointments in its
   * container (H1: admin-managed package — the form cannot express it, the
   * sweep must never touch it) AND its existing appointment (if any) is
   * representable (laneId AND treatmentId present — booking rows require
   * treatmentId, so a treatmentless legacy appointment could never be
   * re-emitted and a governed empty set would sweep-cancel it). Failed /
   * pending envelopes, REQUEST_BASED blocks and unrepresentable states stay
   * OUT of `governedProgramItemIds` — the worker's cancel sweep can never
   * touch them, and their cards render non-interactive (honest: a pick
   * there could not be submitted). With no governed block at all the
   * hidden input is omitted entirely → payload omits `offeringBookings`.
   */
  const isOfferingGoverned = (programItemId: string): boolean => {
    const st = offeringStates.get(programItemId);
    if (!st || st.data === null) return false;
    if (st.data.multipleAppointments === true) return false;
    // PB-WB-5: a REQUEST_BASED block is never governed. The form offers no
    // booking and no cancel for it, so a submit could only ever say
    // "bookings:[]" — and the envelope is fetched once at mount, so an
    // appointment the organiser assigns AFTER mount would be swept by
    // that stale empty set. Out of the governed set, the sweep can never
    // touch it (explicit cancel stays DELETE /slotting/appointment/:id).
    if (st.data.bookingMode === "REQUEST_BASED") return false;
    const my = st.data.myAppointment;
    return my === null || (my.laneId !== null && my.treatmentId !== null);
  };

  const governedOfferingIds = visibleOfferings
    .map((o) => o.programItemId)
    .filter((id) => isOfferingGoverned(id));

  const offeringInputValue = useMemo(() => {
    const bookings: OfferingBookingEntry[] = [];
    for (const id of governedOfferingIds) {
      const sel = offeringSelections.get(id);
      if (sel) bookings.push({ programItemId: id, ...sel });
    }
    const payload: OfferingBookingsPayload = {
      governedProgramItemIds: governedOfferingIds,
      bookings,
    };
    return JSON.stringify(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [governedOfferingIds.join(","), offeringSelections]);

  // F2: true when any governed block's live selection differs from its
  // seeded baseline — i.e. THIS submit would actually change offering
  // bookings (new pick, slot change, treatment switch, explicit remove).
  // Serialised into `__offering_dirty` for dataInput's "noChanges" gate
  // (see hasOfferingSelectionChanges).
  const offeringDirty = useMemo(() => {
    for (const id of governedOfferingIds) {
      const a = offeringSelections.get(id);
      const b = offeringBaselines.get(id);
      if (a === undefined && b === undefined) continue;
      if (
        a === undefined ||
        b === undefined ||
        a.treatmentId !== b.treatmentId ||
        a.laneId !== b.laneId ||
        a.startDt !== b.startDt ||
        a.endDt !== b.endDt
      ) {
        return true;
      }
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [governedOfferingIds.join(","), offeringSelections, offeringBaselines]);

  function toggleOfferingExpanded(programItemId: string): void {
    const wasExpanded = expandedOfferings.has(programItemId);
    setExpandedOfferings((prev) => {
      const next = new Set(prev);
      if (next.has(programItemId)) next.delete(programItemId);
      else next.add(programItemId);
      return next;
    });
    // Retry a failed envelope on (re-)expand.
    const st = offeringStates.get(programItemId);
    if (st !== undefined && st.error && !st.loading) {
      loadOfferingSlots(programItemId);
      return;
    }
    // Prefill (n>1): the bare mount fetch scopes to no treatment
    // (`selectedTreatmentId` null, slots []), but the seeded selection
    // knows the guest's treatment — expanding fetches its slots so the
    // preselected pill and the picker agree. Lazy on purpose: prefilled
    // blocks the guest never opens cost no second request.
    if (!wasExpanded && st !== undefined && !st.loading && st.data !== null) {
      const sel = offeringSelections.get(programItemId);
      if (
        st.data.selectedTreatmentId === null &&
        sel !== undefined &&
        st.data.treatments.some((tr) => tr.treatmentId === sel.treatmentId)
      ) {
        loadOfferingSlots(programItemId, { treatmentId: sel.treatmentId });
      }
    }
  }

  function isSoldOut(s: SessionPublic): boolean {
    if (fullIds.has(s.programItemId)) return true;
    return s.spotsLeft !== null && s.spotsLeft <= 0;
  }

  function toggle(s: SessionPublic) {
    const id = s.programItemId;
    const has = selectedIds.has(id);
    // Sold-out cards are only interactive to DESELECT (post-409 marking).
    if (!has && isSoldOut(s)) return;
    if (isSingle) {
      setGroupError(null);
      setSelectedIds(has ? new Set<string>() : new Set([id]));
      return;
    }
    if (!has && effectiveMax !== null && selectedIds.size >= effectiveMax) {
      setGroupError(t("sessionMaxError", String(effectiveMax)));
      return;
    }
    const next = new Set(selectedIds);
    if (has) {
      next.delete(id);
      // Deselecting retires the server's 409 verdict for this block.
      // `isSoldOut` consults `fullIds` BEFORE `spotsLeft`, and `toggle`
      // refuses to select a sold-out card — so without this, the deselect
      // the error message asks for is a ONE-WAY DOOR: the guest can never
      // re-pick, even once someone cancels, short of a page reload.
      // Deselect-then-reselect IS the conscious re-pick the 409 demands,
      // and a still-full block simply 409s again.
      if (fullIds.has(id)) {
        setFullIds((prev) => {
          const n = new Set(prev);
          n.delete(id);
          return n;
        });
      }
    } else next.add(id);
    setGroupError(null);
    setSelectedIds(next);
  }

  // WB_VALIDATE_EVENT: min/max + not-sold-out (group error idiom, mirrors
  // bookingGroup's handleValidate). WB_SESSION_FULL_EVENT: 409 feedback —
  // mark the full cards, keep them selected so the guest must consciously
  // re-pick, refresh live counts.
  useEffect(() => {
    const form = containerRef.current?.closest("form");
    if (!form) return undefined;

    const handleValidate = (e: Event) => {
      // Hidden step: skip — UNLESS the caller asked for hidden fields too
      // (submit does, because the step pills let a visitor jump past this
      // step entirely and nobody would ever check its `min`).
      const detail = (e as CustomEvent).detail as
        | {
            errorCount: number
            includeHidden?: boolean
            hiddenErrorNodes?: Element[]
          }
        | undefined;
      const includeHidden = detail?.includeHidden === true;
      if (!shouldValidateField(containerRef.current, includeHidden)) return;
      if (!sessions) return; // list never loaded — fail open, worker gates
      // Nothing on offer (empty include-list, or the programme is empty):
      // fail open like the load-failure path above. Enforcing a `min` here
      // would raise an error the guest CANNOT satisfy — there is no card to
      // tick — and hard-lock the whole registration form.
      if (visibleSessions.length === 0) return;
      const count = selectedIds.size;
      let error: string | null = null;
      if (count < effectiveMin) {
        error = t("sessionMinError", String(effectiveMin));
      } else if (effectiveMax !== null && count > effectiveMax) {
        error = t("sessionMaxError", String(effectiveMax));
      } else {
        for (const id of selectedIds) {
          const s = sessionById.get(id);
          if (!s) continue;
          // A seat the guest ALREADY HOLDS reads `spotsLeft: 0` precisely
          // because they hold it. Failing them for that would lock a
          // capacity-1 registrant out of saving any edit at all — so the
          // seeded baseline is exempt from the DISPLAY heuristic.
          //
          // It is NOT exempt from `fullIds`: that set only ever comes from
          // a 409 in which the server itself refused the pick, and
          // suppressing that would loop the guest through a submit the
          // worker keeps rejecting.
          const soldOut = sessionBaseline.has(id)
            ? fullIds.has(id)
            : isSoldOut(s);
          if (soldOut) {
            error = t("sessionSoldOutSelected");
            break;
          }
        }
      }
      setGroupError(error);
      if (error) reportValidationError(detail, containerRef.current);
    };

    const handleSessionFull = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { fullProgramItemIds?: string[] }
        | undefined;
      const ids = Array.isArray(detail?.fullProgramItemIds)
        ? detail.fullProgramItemIds
        : [];
      setFullIds(new Set(ids));
      setGroupError(t("sessionFullError"));
      // Refresh live counts so seats-left reflects the race we just lost.
      // Same kinds opt-in as the mount fetch — a bare /sessions here would
      // hit the worker's SESSION-only default filter and wipe the
      // OFFERING/BREAK rows mid-flow.
      fetchSessions(["SESSION", "OFFERING", "BREAK"])
        .then((list) => {
          setSessions(list);
          // Keep the capability probe in step with the list it describes —
          // otherwise a refetch that lands before the mount fetch leaves
          // `seedKnown` stale and the wire shape wrong.
          setSeedKnown(list.some((s) => "selected" in s));
        })
        .catch(() => {
          /* keep stale list */
        });
    };

    // WB_OFFERING_CONFLICT_EVENT: 409 offeringSlotConflict / 400
    // offeringNotDirectlyBookable / 409 offeringMultipleAppointments
    // feedback (mirror of handleSessionFull). The failed submit changed
    // nothing durable server-side (created bookings compensated,
    // reschedules reinstated best-effort, sweep-cancels post-commit only)
    // — refetch the block's slots (which re-seeds the pick from the
    // surviving appointment — including its treatmentId, so pill and
    // slots stay in sync — or clears it) and force a conscious re-pick. `notBookable` refetches too: the fresh envelope's
    // bookingMode flips the card into its "on request" info state.
    // `multipleAppointments` likewise: the fresh envelope carries
    // `multipleAppointments: true` → the card flips into its
    // non-interactive info state and leaves the governed set.
    const handleOfferingConflict = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { programItemId?: string; reason?: string }
        | undefined;
      const id =
        typeof detail?.programItemId === "string" ? detail.programItemId : "";
      if (id === "") return;
      setExpandedOfferings((prev) => new Set(prev).add(id));
      setGroupError(
        detail?.reason === "notBookable"
          ? t("offeringNotBookableError")
          : detail?.reason === "multipleAppointments"
            ? t("offeringMultipleAppointmentsError")
            : t("offeringConflictError"),
      );
      loadOfferingSlots(id, { reseed: true });
    };

    form.addEventListener(WB_VALIDATE_EVENT, handleValidate);
    form.addEventListener(WB_SESSION_FULL_EVENT, handleSessionFull);
    form.addEventListener(WB_OFFERING_CONFLICT_EVENT, handleOfferingConflict);
    return () => {
      form.removeEventListener(WB_VALIDATE_EVENT, handleValidate);
      form.removeEventListener(WB_SESSION_FULL_EVENT, handleSessionFull);
      form.removeEventListener(
        WB_OFFERING_CONFLICT_EVENT,
        handleOfferingConflict,
      );
    };
  }, [
    sessions,
    visibleSessions,
    selectedIds,
    sessionBaseline,
    effectiveMin,
    effectiveMax,
    sessionById,
    fullIds,
  ]);

  // WB_SUBMIT_SUCCESS_EVENT: the just-submitted set IS the server state
  // now — re-baseline change detection at it ("noChanges" is honest again
  // for a follow-up no-touch submit) and clear the "explicitly removed"
  // flags (F1: the cancels are persisted; an appointment surfacing in a
  // LATER refetch — e.g. admin-booked in the meantime — must seed again,
  // or the next submit would silently sweep-cancel it).
  useEffect(() => {
    const form = containerRef.current?.closest("form");
    if (!form) return undefined;
    const handleSubmitSuccess = () => {
      explicitlyRemovedRef.current.clear();
      // A committed submit invalidates every prior 409 verdict.
      setFullIds(new Set());
      setOfferingBaselines(new Map(offeringSelections));
      // Same rule for sessions: what we just submitted is now the server
      // state, so `__session_dirty` must go quiet until the guest touches
      // the picker again (and the new baseline also becomes the sold-out
      // exemption set).
      setSessionBaseline(new Set(selectedIds));
    };
    form.addEventListener(WB_SUBMIT_SUCCESS_EVENT, handleSubmitSuccess);
    return () => {
      form.removeEventListener(WB_SUBMIT_SUCCESS_EVENT, handleSubmitSuccess);
    };
  }, [offeringSelections, selectedIds]);

  // Visibility gate — after all hooks (house rule).
  if (isVisible === false) return null;

  /* ---- chrome ---- */
  const containerStyle: React.CSSProperties = {};
  if (backgroundColor) containerStyle.backgroundColor = backgroundColor;
  if (padding) containerStyle.padding = parsePaddingPx(padding);
  if (border) {
    const bw = borderWidth ? parseInt(borderWidth) + "px" : "1px";
    const bs = borderStyle || "solid";
    const bc = borderColor || "var(--wb-secondary, #e2e8f0)";
    if (borderSides?.length) {
      if (borderSides.includes("top")) containerStyle.borderTop = `${bw} ${bs} ${bc}`;
      if (borderSides.includes("right")) containerStyle.borderRight = `${bw} ${bs} ${bc}`;
      if (borderSides.includes("bottom")) containerStyle.borderBottom = `${bw} ${bs} ${bc}`;
      if (borderSides.includes("left")) containerStyle.borderLeft = `${bw} ${bs} ${bc}`;
    } else {
      containerStyle.border = `${bw} ${bs} ${bc}`;
    }
  }
  if (borderRadius)
    containerStyle.borderRadius = parseBorderRadiusPxLocal(borderRadius) + "px";

  const mutedColor = "var(--wb-primary, #64748b)";
  const secondaryColor = "var(--wb-secondary, #2563eb)";
  const rounding = cardBorderRadius
    ? parseBorderRadiusPxLocal(cardBorderRadius) + "px"
    : "8px";
  const selectedBorder = selectionActiveBorderColor
    ? `2px solid ${selectionActiveBorderColor}`
    : `2px solid ${secondaryColor}`;
  const defaultBorder = selectionBorderColor
    ? `1px solid ${selectionBorderColor}`
    : "1px solid color-mix(in srgb, var(--wb-secondary, #e2e8f0) 30%, transparent)";
  const selectedBg =
    selectionActiveBackgroundColor ||
    "color-mix(in srgb, var(--wb-secondary, #2563eb) 5%, transparent)";

  const timeLabel = (s: SessionPublic): string => {
    if (!s.startTime) return "";
    const start = new Date(s.startTime).toLocaleTimeString(undefined, {
      timeZone: eventTz,
      hour: "2-digit",
      minute: "2-digit",
    });
    if (!s.endTime) return start;
    const end = new Date(s.endTime).toLocaleTimeString(undefined, {
      timeZone: eventTz,
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${start} – ${end}`;
  };

  /** "Wed, 3 Sep, 10:00 – 10:30" — selected-slot summary in event tz. */
  const slotLabel = (startIso: string, endIso: string): string => {
    const day = new Date(startIso).toLocaleDateString(undefined, {
      timeZone: eventTz,
      weekday: "short",
      day: "numeric",
      month: "short",
    });
    const start = new Date(startIso).toLocaleTimeString(undefined, {
      timeZone: eventTz,
      hour: "2-digit",
      minute: "2-digit",
    });
    const end = new Date(endIso).toLocaleTimeString(undefined, {
      timeZone: eventTz,
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${day}, ${start} – ${end}`;
  };

  const linkButtonStyle: React.CSSProperties = {
    alignSelf: "flex-start",
    fontSize: "0.75rem",
    color: secondaryColor,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    textDecoration: "underline",
    textUnderlineOffset: "2px",
  };

  const selectionHints: string[] = [];
  if (effectiveMin > 0) selectionHints.push(t("sessionMinHint", String(effectiveMin)));
  if (effectiveMax !== null) selectionHints.push(t("sessionMaxHint", String(effectiveMax)));

  return (
    <div
      ref={containerRef}
      style={{
        ...containerStyle,
        display: "flex",
        flexDirection: "column",
        gap: resolveFieldSpacing(fieldSpacing),
      }}
    >
      {groupLabel && (
        <span
          style={{
            fontWeight: 500,
            fontSize: "0.875rem",
            color: groupLabelColor || undefined,
          }}
        >
          {groupLabel}
        </span>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
          <div
            style={{
              width: 20,
              height: 20,
              border: "2px solid var(--wb-secondary, #ddd)",
              borderTopColor: "var(--wb-primary, #121212)",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
        </div>
      ) : loadError || !sessions ? (
        // Load failure: NO hidden input rendered → payload omits `sessions`
        // → existing picks stay untouched server-side.
        <div
          style={{
            textAlign: "center",
            padding: "24px 0",
            color: mutedColor,
            opacity: 0.6,
            fontSize: "0.875rem",
          }}
        >
          {t("sessionsUnavailable")}
        </div>
      ) : visibleSessions.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "24px 0",
            color: mutedColor,
            opacity: 0.6,
            fontSize: "0.875rem",
          }}
        >
          {t("sessionsEmpty")}
        </div>
      ) : (
        <>
          {/* Session picks. Against a seed-capable worker this is the
              governed envelope {governedProgramItemIds, selected}, so the
              release is scoped to the blocks this form actually rendered.
              Against an older worker it degrades to the legacy CSV, which
              that worker treats as acquire-only — a widget with no seed
              must never be able to express a release. */}
          <input
            type="hidden"
            name="__session_selected"
            value={
              seedKnown
                ? JSON.stringify({
                    governedProgramItemIds: governedSessionIds,
                    // MUST be a subset of the governed set — the worker
                    // rejects the whole submit otherwise (400), and the
                    // guest cannot recover without a full reload. A pick
                    // can fall out of `visibleSessions` after selection
                    // (admin deletes the block, it re-kinds, or it slips
                    // past the worker's list cap), so filter at emit time.
                    // The offering twin already does exactly this.
                    selected: Array.from(selectedIds).filter((id) =>
                      governedSessionIds.includes(id),
                    ),
                  })
                : Array.from(selectedIds).join(",")
            }
          />
          {/* Change marker for dataInput's edit-mode "noChanges" gate —
              twin of `__offering_dirty`. Without it a session-only edit
              early-returns with a green "no changes" and never submits. */}
          <input
            type="hidden"
            name="__session_dirty"
            value={sessionDirty ? "1" : "0"}
          />

          {/* OFFERING bookings — {governedProgramItemIds, bookings} JSON
              (governed-scope wire contract); rendered once ANY block is
              governed. Unready blocks stay outside the governed set and
              can never be swept. */}
          {governedOfferingIds.length > 0 ? (
            <>
              <input
                type="hidden"
                name="__offering_selected"
                value={offeringInputValue}
              />
              {/* F2: change marker for dataInput's edit-mode "noChanges"
                  gate — "1" when the governed selections differ from the
                  seeded (server) baseline. */}
              <input
                type="hidden"
                name="__offering_dirty"
                value={offeringDirty ? "1" : "0"}
              />
            </>
          ) : null}

          {dayGroups.map(([day, daySessions]) => (
            <div
              key={day || "__undated"}
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              {day ? (
                <div
                  data-testid="session-day-header"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: "var(--wb-primary, #121212)",
                    }}
                  >
                    {day}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: 1,
                      backgroundColor: secondaryColor,
                      opacity: 0.25,
                    }}
                  />
                </div>
              ) : null}

              {daySessions.map((s) => {
                // BREAK — non-interactive schedule row (never a pick
                // target; no checkbox, no role, no hidden-input entry).
                if (s.kind === "BREAK") {
                  return (
                    <div
                      key={s.programItemId}
                      data-testid={`session-break-${s.programItemId}`}
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        alignItems: "baseline",
                        padding: "8px 12px",
                        borderRadius: rounding,
                        color: mutedColor,
                        opacity: 0.8,
                        fontSize: "0.8125rem",
                      }}
                    >
                      {s.startTime ? <span>{timeLabel(s)}</span> : null}
                      <span style={{ fontWeight: 500 }}>{s.title ?? ""}</span>
                      {s.location ? <span>· {s.location}</span> : null}
                    </div>
                  );
                }

                // OFFERING — slot-booking card (W3). Selection = a picked
                // slot (SlotPicker), not a checkbox; AUTO_BOOKABLE shows
                // the lazy-expanded picker, REQUEST_BASED an "on request"
                // info state (the worker rejects direct books there).
                if (s.kind === "OFFERING") {
                  const id = s.programItemId;
                  const st = offeringStates.get(id);
                  const sel = offeringSelections.get(id) ?? null;
                  const expanded = expandedOfferings.has(id);
                  const data = st?.data ?? null;
                  // Two-step (1:n pivot): the active pill is what the
                  // envelope's slots are scoped to; before the prefill's
                  // scoped refetch resolves, the seeded selection's
                  // treatment keeps the pill highlighted.
                  const treatments = data?.treatments ?? [];
                  const multiTreatment = treatments.length > 1;
                  const activeTreatmentId =
                    data === null
                      ? null
                      : (data.selectedTreatmentId ?? sel?.treatmentId ?? null);
                  const activeTreatment =
                    treatments.find(
                      (tr) => tr.treatmentId === activeTreatmentId,
                    ) ?? null;
                  return (
                    <div
                      key={id}
                      data-testid={`offering-card-${id}`}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        padding: 12,
                        borderRadius: rounding,
                        border: sel ? selectedBorder : defaultBorder,
                        background: sel ? selectedBg : "transparent",
                        textAlign: "left",
                      }}
                    >
                      {/* Time · location row (session-card idiom) */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          alignItems: "baseline",
                          fontSize: "0.75rem",
                          color: mutedColor,
                          opacity: 0.8,
                        }}
                      >
                        {s.startTime ? <span>{timeLabel(s)}</span> : null}
                        {s.location ? <span>· {s.location}</span> : null}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{ fontWeight: 500, fontSize: "0.9375rem" }}
                        >
                          {s.title ?? ""}
                        </span>
                        {typeof s.durationMinutes === "number" &&
                        s.durationMinutes > 0 ? (
                          <span
                            style={{
                              fontSize: "0.6875rem",
                              fontWeight: 500,
                              padding: "2px 8px",
                              borderRadius: 999,
                              border: `1px solid color-mix(in srgb, ${secondaryColor} 35%, transparent)`,
                              color: secondaryColor,
                            }}
                          >
                            {s.durationMinutes} min
                          </span>
                        ) : null}
                      </div>

                      {showDescription !== false && s.description ? (
                        <div
                          style={{
                            fontSize: "0.8125rem",
                            color: mutedColor,
                            opacity: 0.75,
                          }}
                        >
                          {s.description}
                        </div>
                      ) : null}

                      {st === undefined || (st.loading && data === null) ? (
                        // Pending envelope — NOT governed yet: keep the
                        // card non-interactive (a pick here could not be
                        // submitted).
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            border: "2px solid var(--wb-secondary, #ddd)",
                            borderTopColor: "var(--wb-primary, #121212)",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        />
                      ) : data !== null &&
                        data.multipleAppointments === true ? (
                        // H1: the guest holds ≥2 active appointments in
                        // this block's container (admin-managed package) —
                        // non-interactive info state; the block is
                        // UNGOVERNED, so this submit can neither rebook
                        // nor sweep-cancel any of them.
                        <div
                          data-testid={`offering-multi-${id}`}
                          style={{
                            fontSize: "0.8125rem",
                            color: mutedColor,
                          }}
                        >
                          {t("offeringMultipleAppointments")}
                        </div>
                      ) : st.error || data === null ? (
                        // Failed envelope — the block is outside the
                        // governed set (its booking is safe from the
                        // sweep), so no interactive picker: honest
                        // unavailable state with a retry.
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            alignItems: "baseline",
                          }}
                        >
                          <span
                            data-testid={`offering-slots-unavailable-${id}`}
                            style={{
                              fontSize: "0.8125rem",
                              color: mutedColor,
                              opacity: 0.7,
                            }}
                          >
                            {t("offeringSlotsUnavailable")}
                          </span>
                          <button
                            type="button"
                            data-testid={`offering-retry-${id}`}
                            onClick={() => loadOfferingSlots(id)}
                            style={linkButtonStyle}
                          >
                            {t("offeringRetry")}
                          </button>
                        </div>
                      ) : data.bookingMode === "REQUEST_BASED" ? (
                        // Checked BEFORE the governed gate (label fix): a
                        // laneless/treatmentless appointment keeps the
                        // block ungoverned, but on a REQUEST_BASED block
                        // the honest state is still "on request" — not the
                        // generic unavailable text. Display anchor falls
                        // back to the raw myAppointment for exactly that
                        // case (unrepresentable → sel was never seeded).
                        <div
                          data-testid={`offering-on-request-${id}`}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                            fontSize: "0.8125rem",
                            color: mutedColor,
                          }}
                        >
                          <span>{t("offeringOnRequest")}</span>
                          {(() => {
                            const anchor = sel ?? data.myAppointment;
                            return anchor !== null ? (
                              <span style={{ color: secondaryColor }}>
                                {t("offeringCurrentBooking")}:{" "}
                                {slotLabel(anchor.startDt, anchor.endDt)}
                              </span>
                            ) : null;
                          })()}
                        </div>
                      ) : !isOfferingGoverned(id) ? (
                        // Unrepresentable existing appointment (laneId or
                        // treatmentId null) on an AUTO_BOOKABLE block —
                        // ungoverned (safe from the sweep), non-interactive
                        // unavailable state; nothing to retry.
                        <span
                          data-testid={`offering-slots-unavailable-${id}`}
                          style={{
                            fontSize: "0.8125rem",
                            color: mutedColor,
                            opacity: 0.7,
                          }}
                        >
                          {t("offeringSlotsUnavailable")}
                        </span>
                      ) : (
                        <>
                          {sel ? (
                            <div
                              data-testid={`offering-selected-${id}`}
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                                alignItems: "baseline",
                                fontSize: "0.8125rem",
                              }}
                            >
                              <span
                                style={{
                                  color: secondaryColor,
                                  fontWeight: 500,
                                }}
                              >
                                {(() => {
                                  const name = multiTreatment
                                    ? treatments.find(
                                        (tr) =>
                                          tr.treatmentId === sel.treatmentId,
                                      )?.name
                                    : undefined;
                                  return `✓ ${name ? `${name} · ` : ""}${slotLabel(sel.startDt, sel.endDt)}`;
                                })()}
                              </span>
                              <button
                                type="button"
                                data-testid={`offering-clear-${id}`}
                                onClick={() => {
                                  // F1: remember the EXPLICIT remove — the
                                  // seed guard must not resurrect the row
                                  // from `myAppointment` on the next
                                  // envelope refetch (pill browsing).
                                  explicitlyRemovedRef.current.add(id);
                                  setOfferingSelections((prev) => {
                                    const next = new Map(prev);
                                    next.delete(id);
                                    return next;
                                  });
                                }}
                                style={linkButtonStyle}
                              >
                                {t("offeringRemoveSelection")}
                              </button>
                            </div>
                          ) : null}
                          {treatments.length === 0 ? (
                            // F6: 0-Terminart container — nothing behind
                            // the expander could ever be picked (no pills,
                            // no slots): suppress it and say so instead of
                            // a mute dead-end.
                            <span
                              data-testid={`offering-no-types-${id}`}
                              style={{
                                fontSize: "0.8125rem",
                                color: mutedColor,
                                opacity: 0.7,
                              }}
                            >
                              {t("offeringNoTypes")}
                            </span>
                          ) : (
                            <button
                              type="button"
                              data-testid={`offering-expand-${id}`}
                              aria-expanded={expanded}
                              onClick={() => toggleOfferingExpanded(id)}
                              style={linkButtonStyle}
                            >
                              {expanded
                                ? t("offeringHideSlots")
                                : sel
                                  ? t("offeringChangeSlot")
                                  : t("offeringChooseSlot")}
                            </button>
                          )}
                          {expanded && treatments.length > 0 ? (
                            <>
                              {multiTreatment ? (
                                // Step 1: Terminart pills (slottingBooking
                                // treatment-select idiom, wb-token colors).
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.8125rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {t("offeringChooseTreatment")}
                                  </span>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 8,
                                    }}
                                  >
                                    {treatments.map((tr) => {
                                      const active =
                                        tr.treatmentId === activeTreatmentId;
                                      return (
                                        <button
                                          key={tr.treatmentId}
                                          type="button"
                                          data-testid={`offering-treatment-${id}-${tr.treatmentId}`}
                                          aria-pressed={active}
                                          onClick={() => {
                                            if (
                                              tr.treatmentId ===
                                                data.selectedTreatmentId ||
                                              st.loading
                                            )
                                              return;
                                            // Step 2 fetch: swap slots +
                                            // duration to this treatment.
                                            loadOfferingSlots(id, {
                                              treatmentId: tr.treatmentId,
                                            });
                                          }}
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "6px 12px",
                                            borderRadius: 999,
                                            fontSize: "0.8125rem",
                                            cursor: "pointer",
                                            border: active
                                              ? selectedBorder
                                              : defaultBorder,
                                            background: active
                                              ? selectedBg
                                              : "transparent",
                                          }}
                                        >
                                          <span style={{ fontWeight: 500 }}>
                                            {tr.name}
                                          </span>
                                          <span
                                            style={{
                                              fontSize: "0.6875rem",
                                              fontWeight: 500,
                                              padding: "1px 6px",
                                              borderRadius: 999,
                                              border: `1px solid color-mix(in srgb, ${secondaryColor} 35%, transparent)`,
                                              color: secondaryColor,
                                            }}
                                          >
                                            {tr.durationMinutes} min
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : null}
                              {st.loading ? (
                                // Scoped refetch in flight — never show the
                                // previous treatment's slots (pill/slot
                                // desync).
                                <div
                                  style={{
                                    width: 16,
                                    height: 16,
                                    border:
                                      "2px solid var(--wb-secondary, #ddd)",
                                    borderTopColor:
                                      "var(--wb-primary, #121212)",
                                    borderRadius: "50%",
                                    animation: "spin 1s linear infinite",
                                  }}
                                />
                              ) : data.selectedTreatmentId !== null ? (
                                <>
                                  <SlotPicker
                                    slots={data.slots}
                                    selected={
                                      sel !== null &&
                                      sel.treatmentId ===
                                        data.selectedTreatmentId
                                        ? sel
                                        : null
                                    }
                                    onSelect={(slot) => {
                                      setGroupError(null);
                                      const chosen = data.selectedTreatmentId;
                                      // F1: toggling the picked slot OFF is
                                      // the remove button's twin — same
                                      // explicit-removal flag; a fresh pick
                                      // clears it.
                                      if (slot === null || chosen === null) {
                                        explicitlyRemovedRef.current.add(id);
                                      } else {
                                        explicitlyRemovedRef.current.delete(
                                          id,
                                        );
                                      }
                                      setOfferingSelections((prev) => {
                                        const next = new Map(prev);
                                        if (slot === null || chosen === null)
                                          next.delete(id);
                                        else
                                          next.set(id, {
                                            treatmentId: chosen,
                                            ...slot,
                                          });
                                        return next;
                                      });
                                    }}
                                    {...(activeTreatment?.durationMinutes !==
                                    undefined
                                      ? {
                                          durationMinutes:
                                            activeTreatment.durationMinutes,
                                        }
                                      : {})}
                                  />
                                  {multiTreatment && sel === null ? (
                                    // Pill picked but no slot yet — this
                                    // emits NO booking row; say so instead
                                    // of silently no-opping on submit.
                                    <span
                                      data-testid={`offering-pick-slot-hint-${id}`}
                                      style={{
                                        fontSize: "0.75rem",
                                        color: mutedColor,
                                        opacity: 0.8,
                                      }}
                                    >
                                      {t("offeringPickSlotHint")}
                                    </span>
                                  ) : null}
                                </>
                              ) : null}
                            </>
                          ) : null}
                        </>
                      )}
                    </div>
                  );
                }

                const selected = selectedIds.has(s.programItemId);
                const soldOut = isSoldOut(s);
                const disabled = soldOut && !selected;
                const description = s.description ?? "";
                const collapsible =
                  showDescription !== false &&
                  description.length > DESCRIPTION_COLLAPSE_CHARS;
                const expanded = expandedIds.has(s.programItemId);
                const shownDescription =
                  collapsible && !expanded
                    ? description.slice(0, DESCRIPTION_COLLAPSE_CHARS).trimEnd() + "…"
                    : description;
                return (
                  <div
                    key={s.programItemId}
                    data-testid={`session-card-${s.programItemId}`}
                    role={isSingle ? "radio" : "checkbox"}
                    aria-checked={selected}
                    aria-disabled={disabled}
                    tabIndex={disabled ? -1 : 0}
                    onClick={() => {
                      if (!disabled) toggle(s);
                    }}
                    onKeyDown={(e) => {
                      if (disabled) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle(s);
                      }
                    }}
                    style={{
                      display: "flex",
                      gap: 12,
                      padding: 12,
                      borderRadius: rounding,
                      border: selected ? selectedBorder : defaultBorder,
                      background: selected ? selectedBg : "transparent",
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.55 : 1,
                      textAlign: "left",
                    }}
                  >
                    {/* Select control: checkbox (multiple) / radio (single) */}
                    <span
                      aria-hidden
                      style={{
                        width: 16,
                        height: 16,
                        marginTop: 2,
                        borderRadius: isSingle ? "50%" : 3,
                        flexShrink: 0,
                        border: selected
                          ? `2px solid ${secondaryColor}`
                          : `2px solid color-mix(in srgb, ${secondaryColor} 40%, transparent)`,
                        background: selected ? secondaryColor : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--wb-background, #fff)",
                        fontSize: 11,
                      }}
                    >
                      {selected && !isSingle ? "✓" : ""}
                    </span>

                    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}>
                      {/* Time · location row */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          alignItems: "baseline",
                          fontSize: "0.75rem",
                          color: mutedColor,
                          opacity: 0.8,
                        }}
                      >
                        {s.startTime ? <span>{timeLabel(s)}</span> : null}
                        {s.location ? <span>· {s.location}</span> : null}
                      </div>

                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 500, fontSize: "0.9375rem" }}>
                          {s.title ?? ""}
                        </span>
                        {soldOut ? (
                          <span
                            data-testid="session-sold-out"
                            style={{
                              fontSize: "0.6875rem",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              padding: "2px 8px",
                              borderRadius: 999,
                              background:
                                "color-mix(in srgb, var(--wb-primary, #64748b) 12%, transparent)",
                              color: mutedColor,
                            }}
                          >
                            {t("sessionSoldOut")}
                          </span>
                        ) : s.spotsLeft !== null ? (
                          <span
                            data-testid="session-seats-left"
                            style={{
                              fontSize: "0.6875rem",
                              fontWeight: 500,
                              padding: "2px 8px",
                              borderRadius: 999,
                              border: `1px solid color-mix(in srgb, ${secondaryColor} 35%, transparent)`,
                              color: secondaryColor,
                            }}
                          >
                            {t("sessionSeatsLeft", String(s.spotsLeft))}
                          </span>
                        ) : null}
                      </div>

                      {/* Speaker chips */}
                      {showSpeakers !== false && s.speakers.length > 0 ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {s.speakers.map((sp) => (
                            <span
                              key={sp.id}
                              data-testid="session-speaker-chip"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: "0.75rem",
                                color: mutedColor,
                                border: defaultBorder,
                                borderRadius: 999,
                                padding: "2px 8px 2px 2px",
                              }}
                            >
                              {sp.photo ? (
                                <img
                                  src={sp.photo}
                                  alt=""
                                  style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    flexShrink: 0,
                                  }}
                                />
                              ) : (
                                <span
                                  aria-hidden
                                  style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    background:
                                      "color-mix(in srgb, var(--wb-secondary, #2563eb) 15%, transparent)",
                                    color: secondaryColor,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 10,
                                    fontWeight: 600,
                                    flexShrink: 0,
                                  }}
                                >
                                  {(sp.name ?? "?").charAt(0).toUpperCase()}
                                </span>
                              )}
                              <span>{sp.name ?? ""}</span>
                            </span>
                          ))}
                        </div>
                      ) : null}

                      {/* Description (collapsible when long) */}
                      {showDescription !== false && description ? (
                        <div style={{ fontSize: "0.8125rem", color: mutedColor, opacity: 0.75 }}>
                          <span>{shownDescription}</span>
                          {collapsible ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(s.programItemId))
                                    next.delete(s.programItemId);
                                  else next.add(s.programItemId);
                                  return next;
                                });
                              }}
                              style={{
                                marginLeft: 6,
                                fontSize: "0.75rem",
                                color: secondaryColor,
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: 0,
                                textDecoration: "underline",
                                textUnderlineOffset: "2px",
                              }}
                            >
                              {expanded ? t("sessionShowLess") : t("sessionShowMore")}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Selection count vs min/max */}
          <div
            data-testid="session-selection-count"
            style={{ fontSize: "0.75rem", color: mutedColor, opacity: 0.8 }}
          >
            {t("sessionSelectedCount", String(selectedIds.size))}
            {selectionHints.length > 0 ? ` · ${selectionHints.join(" · ")}` : ""}
          </div>

          {/* Group error (validation / 409 sessionFull) */}
          {groupError ? (
            <span
              data-testid="session-group-error"
              style={{ fontSize: "0.75rem", color: "#ef4444" }}
            >
              {groupError}
            </span>
          ) : null}
        </>
      )}
    </div>
  );
}
